import React, { useState, useMemo } from 'react';
import { H5, H6 } from '../../../../AbstractElements';
import { FormGroup, Input, Label, FormFeedback, Row, Col, Button, Spinner, Alert } from 'reactstrap';
import { toast } from 'react-toastify';
import { PlusCircle } from 'react-feather';
import { createAccidentFileFromInsurance } from '../../../../Api/ApiMethods';
import FilesComponent from '../Component/FilesComponent';
import { getFieldAccess } from '../../../../_helper/fieldAccess';
import { isNoInsuranceProvider, shouldRequirePolicyDetails } from '../../../../_helper/mandatoryFieldRules';
import { normalizeFieldValue, normalizeDigitsOnly, normalizeDecimal, validateLicensePlate, displayDecimal } from '../../../../_helper/regexHandler';
import AddLorOnlyFileModal from './AddLorOnlyFileModal';

const ExpertForm = ({ formData, errors, setFormData, handleInputChange, handleAddWorkFile, handleRemoveWorkFile, handleCreateAccidentFile, workFileTypes, experts, handleExpertChange, insuranceCompanies, callFormState, fieldPermissions, canEdit = true, accidentFileId, creatingAccidentFileIndex, accidentFiles = [], callFormId }) => {
    const [isLorOnlyModalOpen, setIsLorOnlyModalOpen] = useState(false);

    const hasAnyLorAccidentFile = useMemo(() => {
        const lorTitleMatches = (title) => {
            if (!title) return false;
            const t = String(title).toLowerCase();
            return t.includes('hak mahrumiyeti') || t.includes('mahrumiyeti') || t.includes('lor');
        };
        const lorTypeIds = new Set(
            (workFileTypes || [])
                .filter(wft => lorTitleMatches(wft.title) || lorTitleMatches(wft.name))
                .map(wft => wft.id)
        );
        if (!lorTypeIds.size) return false;
        return (accidentFiles || []).some(af =>
            (af.filesToBeWorked || []).some(ftw =>
                (ftw.fileType || []).some(ft => {
                    const id = typeof ft === 'string' ? ft : ft?.id;
                    return id && lorTypeIds.has(id);
                })
            )
        );
    }, [accidentFiles, workFileTypes]);

    const showLorShortcutButton = callFormState === 'Sigorta Başvuru Dilekçe Bekleniyor'
        && !hasAnyLorAccidentFile
        && canEdit
        && Boolean(callFormId);

    const handleLorCreated = () => {
        setTimeout(() => window.location.reload(), 1000);
    };
    const [newFile, setNewFile] = useState({
        fileType: [],
        fileTypeExpenses: {},
        withReport: true,
        withFault: false,
        withoutReport: false,
        numberOfDays: '',
        expensePerDay: ''
    });
    const [newFileErrors, setNewFileErrors] = useState({});

    const [, setExpertReportFiles] = useState({});
    const [, setInsuranceApplicationFiles] = useState({});
    const [creatingInsuranceRequests, setCreatingInsuranceRequests] = useState({});

    const reapplicationStates = ['Yeni Eksper İşlem Bekleniyor', 'Yeni Eksper Rapor Bekleniyor', 'Yeni Eksper Raporu Ödeme Bekleniyor', 'Yeni Sigorta Başvuru Dilekçe Bekleniyor', 'Ekspersiz Yeni Sigorta Başvuru Dilekçe Bekleniyor'];
    const insuranceOnlyState = 'Ekspersiz Yeni Sigorta Başvuru Dilekçe Bekleniyor';
    const isInsuranceOnlyWorkflow = callFormState === insuranceOnlyState;
    const isReapplicationWorkflowState = reapplicationStates.includes(callFormState);
    const requiresReapplicationFlag = isReapplicationWorkflowState && !isInsuranceOnlyWorkflow;

    const addWorkFileAccess = getFieldAccess(fieldPermissions, 'accidentfile.filestobeworked.add', canEdit);
    const removeWorkFileAccess = getFieldAccess(fieldPermissions, 'accidentfile.filestobeworked.remove', canEdit);
    const fileTypeAccess = getFieldAccess(fieldPermissions, 'accidentfile.filestobeworked.filetype', canEdit);
    const fileExpenseAccess = getFieldAccess(fieldPermissions, 'accidentfile.filestobeworked.fileexpense', canEdit);
    const numberOfDaysAccess = getFieldAccess(fieldPermissions, 'accidentfile.filestobeworked.numberofdays', canEdit);
    const expensePerDayAccess = getFieldAccess(fieldPermissions, 'accidentfile.filestobeworked.expenseperday', canEdit);
    const withReportAccess = getFieldAccess(fieldPermissions, 'accidentfile.filestobeworked.withreport', canEdit);
    const withFaultAccess = getFieldAccess(fieldPermissions, 'accidentfile.filestobeworked.withfault', canEdit);
    const withoutReportAccess = getFieldAccess(fieldPermissions, 'accidentfile.filestobeworked.withoutreport', canEdit);
    const isOpenDamageFileAccess = getFieldAccess(fieldPermissions, 'accidentfile.isthereopendamagefile', canEdit);
    const whoseInsuranceAccess = getFieldAccess(fieldPermissions, 'accidentfile.whoseinsurance', canEdit);
    const ourFaultRateAccess = getFieldAccess(fieldPermissions, 'accident.ourfaultrate', canEdit);
    const clientTrafficAccess = getFieldAccess(fieldPermissions, 'licenseplates.trafficinsurancepolicy', canEdit);
    const clientCascoAccess = getFieldAccess(fieldPermissions, 'licenseplates.cascoinsurancepolicy', canEdit);
    const counterpartyPlateAccess = getFieldAccess(fieldPermissions, 'accidents.counterpartylicenseplatenumber', canEdit);
    const counterpartyFaultRateAccess = getFieldAccess(fieldPermissions, 'accident.counterpartyfaultrate', canEdit);
    const cpTrafficAccess = getFieldAccess(fieldPermissions, 'accidents.counterpartytrafficinsurancepolicy', canEdit);
    const cpCascoAccess = getFieldAccess(fieldPermissions, 'accidents.counterpartycascoinsurancepolicy', canEdit);
    const clientTrafficStartAccess = getFieldAccess(fieldPermissions, 'licenseplates.trafficinsurancestart', canEdit);
    const clientCascoStartAccess = getFieldAccess(fieldPermissions, 'licenseplates.cascoinsurancestart', canEdit);
    const cpTrafficStartAccess = getFieldAccess(fieldPermissions, 'accidents.counterpartytrafficinsurancestart', canEdit);
    const cpCascoStartAccess = getFieldAccess(fieldPermissions, 'accidents.counterpartycascoinsurancestart', canEdit);
    const clientTrafficDetailsRequired = clientTrafficAccess.required && shouldRequirePolicyDetails(formData.trafficInsurancePolicy, insuranceCompanies);
    const clientCascoDetailsRequired = clientCascoAccess.required && shouldRequirePolicyDetails(formData.cascoInsurancePolicy, insuranceCompanies, 'cascoInsuranceProvider');
    const cpTrafficDetailsRequired = cpTrafficAccess.required && shouldRequirePolicyDetails(formData.counterpartyTrafficInsurancePolicy, insuranceCompanies);
    const cpCascoDetailsRequired = cpCascoAccess.required && shouldRequirePolicyDetails(formData.counterpartyCascoInsurancePolicy, insuranceCompanies, 'cascoInsuranceProvider');
    const clientTrafficIsNoInsurance = isNoInsuranceProvider(formData.trafficInsurancePolicy?.trafficInsuranceProvider?.[0], insuranceCompanies);
    const clientCascoIsNoInsurance = isNoInsuranceProvider(formData.cascoInsurancePolicy?.cascoInsuranceProvider?.[0], insuranceCompanies);
    const cpTrafficIsNoInsurance = isNoInsuranceProvider(formData.counterpartyTrafficInsurancePolicy?.trafficInsuranceProvider?.[0], insuranceCompanies);
    const cpCascoIsNoInsurance = isNoInsuranceProvider(formData.counterpartyCascoInsurancePolicy?.cascoInsuranceProvider?.[0], insuranceCompanies);

    const handleChange = (field, value) => {
        setNewFileErrors(prev => ({ ...prev, [field]: '' }));
        setNewFile((prev) => {
            const updatedFile = { ...prev, [field]: value };

            if (updatedFile.fileType.some(typeId => isLorFileType(typeId)) && (field === 'numberOfDays' || field === 'expensePerDay')) {
                const days = parseInt(field === 'numberOfDays' ? value : updatedFile.numberOfDays) || 0;
                const ratePerDay = parseFloat(field === 'expensePerDay' ? value : updatedFile.expensePerDay) || 0;

                const totalExpense = days * ratePerDay;
                updatedFile.fileExpense = totalExpense.toString();
            }

            return updatedFile;
        });
    };

    const isLorFileType = (fileTypeIdOrObj) => {
        if (!fileTypeIdOrObj) return false;

        if (typeof fileTypeIdOrObj === 'object' && fileTypeIdOrObj.title) {
            const title = fileTypeIdOrObj.title.toLowerCase();
            return title.includes('hak mahrumiyeti') ||
                title.includes('lor') ||
                title.includes('mahrumiyeti');
        }

        if (typeof fileTypeIdOrObj === 'string' || typeof fileTypeIdOrObj === 'number') {
            const fileTypeObj = workFileTypes.find(type => type.id === fileTypeIdOrObj.toString());
            if (fileTypeObj && fileTypeObj.title) {
                const title = fileTypeObj.title.toLowerCase();
                return title.includes('hak mahrumiyeti') ||
                    title.includes('lor') ||
                    title.includes('mahrumiyeti');
            }
        }

        return false;
    };

    const hasLorSelected = newFile.fileType.some(id => isLorFileType(id));
    const hasNonLorSelected = newFile.fileType.some(id => !isLorFileType(id));

    const handleFileTypeChange = (fileTypeId, checked) => {
        setNewFileErrors(prev => ({ ...prev, fileType: '' }));
        let newSelectedTypes = [...newFile.fileType];

        if (checked) {
            if (newSelectedTypes.length >= 2) {
                toast.warning('En fazla 2 dosya türü seçebilirsiniz.');
                return;
            }

            if (isLorFileType(fileTypeId) && formData.willLorBeWorked === "no") {
                toast.warning('Hak Mahrumiyeti çalışılmayacak olarak işaretlendiği için bu dosya türü seçilemez.');
                return;
            }

            if (isLorFileType(fileTypeId) && hasNonLorSelected) {
                toast.warning('Hak Mahrumiyeti başka dosya türleriyle birlikte seçilemez.');
                return;
            }

            if (!isLorFileType(fileTypeId) && hasLorSelected) {
                toast.warning('Hak Mahrumiyeti seçiliyken başka dosya türü eklenemez.');
                return;
            }

            newSelectedTypes.push(fileTypeId);
        } else {
            newSelectedTypes = newSelectedTypes.filter(id => id !== fileTypeId);
            setNewFile(prev => {
                const newExpenses = { ...prev.fileTypeExpenses };
                delete newExpenses[fileTypeId];
                return {
                    ...prev,
                    fileTypeExpenses: newExpenses
                };
            });
        }

        setNewFile(prev => ({
            ...prev,
            fileType: newSelectedTypes
        }));

    };

    const handleFileTypeExpenseChange = (fileTypeId, value) => {
        setNewFileErrors(prev => ({
            ...prev,
            fileTypeExpenses: {
                ...(prev.fileTypeExpenses || {}),
                [fileTypeId]: '',
            },
        }));
        setNewFile(prev => ({
            ...prev,
            fileTypeExpenses: {
                ...prev.fileTypeExpenses,
                [fileTypeId]: value
            }
        }));
    };

    const handleCheckboxChange = (field) => {
        if (field === 'withReport' || field === 'withoutReport') {
            if (field === 'withReport') {
                if (newFile.withReport && !newFile.withoutReport) {
                    return;
                }

                setNewFile((prev) => ({
                    ...prev,
                    withReport: true,
                    withoutReport: false
                }));
            }
            else if (field === 'withoutReport') {
                if (newFile.withoutReport && !newFile.withReport) {
                    return;
                }

                setNewFile((prev) => ({
                    ...prev,
                    withoutReport: true,
                    withReport: false
                }));
            }
        } else {
            setNewFile((prev) => ({ ...prev, [field]: !prev[field] }));
        }
    };

    const handleExpertReportFilesChange = (accidentFileId, files) => {
        setExpertReportFiles(prev => ({
            ...prev,
            [accidentFileId]: files
        }));
        setFormData(prev => ({
            ...prev,
            expertReportFiles: {
                ...(prev.expertReportFiles || {}),
                [accidentFileId]: files,
            },
        }));
    };

    const handleInsuranceApplicationFilesChange = (accidentFileId, files) => {
        setInsuranceApplicationFiles(prev => ({
            ...prev,
            [accidentFileId]: files
        }));
        setFormData(prev => ({
            ...prev,
            insuranceApplicationFiles: {
                ...(prev.insuranceApplicationFiles || {}),
                [accidentFileId]: files,
            },
        }));
    };

    const handleCreateInsuranceApplication = async (accidentFileId) => {
        if (!accidentFileId) {
            toast.error('Yeni sigorta başvurusu oluşturmak için kaza dosyası ID bulunamadı.');
            return;
        }

        setCreatingInsuranceRequests(prev => ({ ...prev, [accidentFileId]: true }));

        try {
            await createAccidentFileFromInsurance({ accidentFileId });
            setTimeout(() => window.location.reload(), 1000);
        } catch (error) {
            console.error('Error creating insurance application from insurance-only workflow:', error);
        } finally {
            setCreatingInsuranceRequests(prev => ({ ...prev, [accidentFileId]: false }));
        }
    };

    const handleAdd = () => {
        if (addWorkFileAccess.disabled) {
            toast.warning('Dosya ekleme yetkiniz bulunmamaktadır.');
            return;
        }

        const lorFileTypes = newFile.fileType.filter(typeId => isLorFileType(typeId));
        const nonLorFileTypes = newFile.fileType.filter(typeId => !isLorFileType(typeId));
        const nextErrors = {};

        if (newFile.fileType.length === 0) {
            nextErrors.fileType = 'En az bir dosya türü seçimi zorunludur.';
        }

        const reorderedFileTypes = [...nonLorFileTypes, ...lorFileTypes];

        const existingFiles = formData.filesToBeWorked || [];

        const newFileTypesString = reorderedFileTypes.sort().join(',');

        const isDuplicate = existingFiles.some(file => {
            let existingFileTypes = [];

            if (Array.isArray(file.fileType)) {
                if (file.fileType.length > 0 && typeof file.fileType[0] === 'object' && file.fileType[0].id) {
                    existingFileTypes = file.fileType.map(ft => ft.id);
                }
                else {
                    existingFileTypes = file.fileType;
                }
            }
            else if (file.fileType && typeof file.fileType === 'object' && !Array.isArray(file.fileType) && file.fileType.id) {
                existingFileTypes = [file.fileType.id];
            }

            const existingFileTypesString = existingFileTypes.sort().join(',');

            return existingFileTypesString === newFileTypesString;
        });

        if (isDuplicate) {
            const fileTypeNames = reorderedFileTypes.map(id => {
                const fileType = workFileTypes.find(type => type.id === id);
                return fileType ? fileType.title : '';
            }).filter(Boolean);

            toast.warning(`Bu dosya türü kombinasyonu zaten eklenmiş: ${fileTypeNames.join(' + ')}`);
            return;
        }

        const hasLorFileType = lorFileTypes.length > 0;

        if (hasLorFileType) {
            if (!newFile.numberOfDays) nextErrors.numberOfDays = 'Gün sayısı zorunludur.';
            if (!newFile.expensePerDay) nextErrors.expensePerDay = 'Gün başı fiyat zorunludur.';

            if (formData.willLorBeWorked === "no") {
                toast.warning('Hak Mahrumiyeti çalışılmayacak olarak işaretlendiği için bu dosya türü eklenemez.');
                return;
            }
        }

        for (const typeId of nonLorFileTypes) {
            if (!newFile.fileTypeExpenses[typeId] || newFile.fileTypeExpenses[typeId].trim() === '') {
                const fileType = workFileTypes.find(type => type.id === typeId);
                const typeName = fileType ? fileType.title : 'Bilinmeyen';
                nextErrors.fileTypeExpenses = {
                    ...(nextErrors.fileTypeExpenses || {}),
                    [typeId]: `${typeName} tutarı zorunludur.`,
                };
            }
        }

        if (Object.keys(nextErrors).length > 0) {
            setNewFileErrors(nextErrors);
            toast.warning('Lütfen zorunlu dosya bilgilerini doldurun.');
            return;
        }

        const fileTypeTitles = reorderedFileTypes.map(id => {
            const fileType = workFileTypes.find(type => type.id === id);
            return fileType ? fileType.title : '';
        });

        const fileTypeExpenseDetails = {};
        reorderedFileTypes.forEach(typeId => {
            if (isLorFileType(typeId)) {
                fileTypeExpenseDetails[typeId] = parseInt(newFile.numberOfDays || 0) * parseFloat(newFile.expensePerDay || 0);
            } else {
                fileTypeExpenseDetails[typeId] = parseFloat((newFile.fileTypeExpenses[typeId] || '').replace(/,/g, '.') || 0) || 0;
            }
        });

        const fileToAdd = {
            fileType: reorderedFileTypes,
            fileTypeExpenseDetails: fileTypeExpenseDetails,
            withReport: newFile.withReport,
            withFault: newFile.withFault,
            withoutReport: newFile.withoutReport,
            numberOfDays: hasLorFileType ? parseInt(newFile.numberOfDays || 0) : 0,
            expensePerDay: hasLorFileType ? parseFloat(newFile.expensePerDay || 0) : 0,
            _fileTypeTitles: fileTypeTitles
        };

        const updatedFiles = [...(formData.filesToBeWorked || []), fileToAdd];
        setFormData(prevFormData => ({
            ...prevFormData,
            filesToBeWorked: updatedFiles
        }));

        setNewFile({
            fileType: [],
            fileTypeExpenses: {},
            withReport: true,
            withFault: false,
            withoutReport: false,
            numberOfDays: '',
            expensePerDay: ''
        });
        setNewFileErrors({});
    };

    return (
        <>
            {['Eksper İşlem Bekleniyor',
                'Yeni Eksper İşlem Bekleniyor',
                'Eksper Rapor Bekleniyor',
                'Eksper Raporu Ödeme Bekleniyor',
                'Sigorta Başvuru Dilekçe Bekleniyor',
                'İcra Sürecine Yönlendirildi',
                'Tüm Form'].includes(callFormState) && (
                    <>
                        <H5>Evrak Talep Bilgileri</H5>
                        <div style={{ height: "20px" }}></div>

                        <FormGroup className="d-flex align-items-center mb-3">
                            <Label for="isThereOpenDamageFileRadio" className="me-3 text-nowrap" style={{ minWidth: '200px' }}>
                                Açık Hasar Dosyası Var Mı? {isOpenDamageFileAccess.required && <span className="text-danger">*</span>}
                            </Label>
                            <div className="d-flex">
                                <div className="radio radio-success me-3 d-flex align-items-center">
                                    <Input
                                        id="isThereOpenDamageFileTrue"
                                        type="radio"
                                        name="isThereOpenDamageFile"
                                        value="yes"
                                        checked={formData.isThereOpenDamageFile === "yes"}
                                        onChange={() =>
                                            setFormData((prevFormData) => ({
                                                ...prevFormData,
                                                isThereOpenDamageFile: "yes",
                                            }))
                                        }
                                        invalid={!!errors.isThereOpenDamageFile}
                                        disabled={isOpenDamageFileAccess.disabled}
                                        required={isOpenDamageFileAccess.required}
                                    />
                                    <Label for="isThereOpenDamageFileTrue" className="ms-1 mb-0">
                                        Evet
                                    </Label>
                                </div>
                                <div className="radio radio-danger d-flex align-items-center">
                                    <Input
                                        id="isThereOpenDamageFileFalse"
                                        type="radio"
                                        name="isThereOpenDamageFile"
                                        value="no"
                                        checked={formData.isThereOpenDamageFile === "no"}
                                        onChange={() =>
                                            setFormData((prevFormData) => ({
                                                ...prevFormData,
                                                isThereOpenDamageFile: "no",
                                            }))
                                        }
                                        invalid={!!errors.isThereOpenDamageFile}
                                        disabled={isOpenDamageFileAccess.disabled}
                                    />
                                    <Label for="isThereOpenDamageFileFalse" className="ms-1 mb-0">
                                        Hayır
                                    </Label>
                                </div>
                            </div>
                        </FormGroup>

                        <FormGroup className="d-flex align-items-center me-2 mt-3 flex-wrap">
                            <Label for="whoseInsurance">Başvuru Yapılacak Yer {whoseInsuranceAccess.required && <span className="text-danger">*</span>}</Label>
                            <Input
                                type="select"
                                id="whoseInsurance"
                                name="whoseInsurance"
                                value={formData.whoseInsurance}
                                onChange={handleInputChange}
                                className="form-control"
                                disabled={whoseInsuranceAccess.disabled}
                                required={whoseInsuranceAccess.required}
                            >
                                <option value="KARŞI TARAF TRAFİK SİGORTA">KARŞI TARAF TRAFİK SİGORTA</option>
                                <option value="KARŞI TARAF KASKO">KARŞI TARAF KASKO</option>
                                <option value="MÜVEKKİL TRAFİK SİGORTA">MÜVEKKİL TRAFİK SİGROTA</option>
                                <option value="MÜVEKKİL KASKO">MÜVEKKİL KASKO</option>
                            </Input>
                        </FormGroup>

                        <div style={{ height: "20px" }}></div>
                        <hr className="mt-4 mb-4" />
                        <div style={{ height: "20px" }}></div>

                        <H6>Müvekkil Sigorta Bilgileri</H6>
                        <div style={{ height: "20px" }}></div>

                        <FormGroup>
                            <Label for="ourFaultRate">Müvekkil Kusur Oranı {ourFaultRateAccess.required && <span className="text-danger">*</span>}</Label>
                            <div className="input-group">
                                <Input
                                    type="text"
                                    id="ourFaultRate"
                                    name="ourFaultRate"
                                    value={displayDecimal(formData.ourFaultRate || '')}
                                    onChange={(e) => {
                                        const value = normalizeFieldValue('ourFaultRate', e.target.value);

                                        handleInputChange({
                                            target: {
                                                name: 'ourFaultRate',
                                                value
                                            }
                                        });

                                        const counterValue = value ? (100 - parseFloat(value)).toString() : '';
                                        handleInputChange({
                                            target: {
                                                name: 'counterpartyFaultRate',
                                                value: counterValue
                                            }
                                        });
                                    }}
                                    placeholder="0-100"
                                    invalid={!!errors.ourFaultRate || (formData.ourFaultRate && (parseFloat(formData.ourFaultRate) > 100 || parseFloat(formData.ourFaultRate) < 0))}
                                    disabled={ourFaultRateAccess.disabled}
                                    required={ourFaultRateAccess.required}
                                />
                                <div className="input-group-append">
                                    <span className="input-group-text">%</span>
                                </div>
                                <FormFeedback>
                                    {errors.ourFaultRate || 'Oran 0-100 arasında olmalıdır'}
                                </FormFeedback>
                            </div>
                        </FormGroup>

                        <FormGroup>
                            <Row>
                                <Col md={3}>
                                    <Label for="trafficInsuranceProvider">Müvekkil Trafik Sigorta Sağlayıcısı {clientTrafficAccess.required && <span className="text-danger">*</span>}</Label>
                                    <Input
                                        type="select"
                                        id="trafficInsuranceProvider"
                                        name="trafficInsuranceProvider"
                                        value={formData.trafficInsurancePolicy?.trafficInsuranceProvider?.[0] || ''}
                                        onChange={(e) => {
                                            const providerId = e.target.value;
                                            const isNoInsurance = isNoInsuranceProvider(providerId, insuranceCompanies);
                                            setFormData((prevFormData) => ({
                                                ...prevFormData,
                                                trafficInsurancePolicy: {
                                                    ...prevFormData.trafficInsurancePolicy,
                                                    trafficInsuranceProvider: providerId === "" ? [] : [providerId],
                                                    ...(isNoInsurance ? {
                                                        trafficInsuranceNumber: '',
                                                        trafficInsuranceStart: null,
                                                        trafficInsuranceExpiry: null,
                                                    } : {})
                                                },
                                            }))
                                        }}
                                        invalid={!!errors.trafficInsuranceProvider}
                                        disabled={clientTrafficAccess.disabled}
                                        required={clientTrafficAccess.required}
                                    >
                                        <option value="">Seçiniz</option>
                                        {insuranceCompanies && insuranceCompanies.map((company) => (
                                            <option key={company.id} value={company.id}>
                                                {company.name}
                                            </option>
                                        ))}
                                    </Input>
                                    <FormFeedback>{errors.trafficInsuranceProvider}</FormFeedback>
                                </Col>
                                <Col md={3}>
                                    <Label for="trafficInsuranceNumber">Müvekkil Trafik Sigorta Numarası {clientTrafficDetailsRequired && <span className="text-danger">*</span>}</Label>
                                    <Input
                                        type="text"
                                        id="trafficInsuranceNumber"
                                        name="trafficInsuranceNumber"
                                        value={formData.trafficInsurancePolicy?.trafficInsuranceNumber || ''}
                                        onChange={(e) => {
                                            const value = normalizeFieldValue('trafficInsuranceNumber', e.target.value);
                                            setFormData((prevFormData) => ({
                                                ...prevFormData,
                                                trafficInsurancePolicy: {
                                                    ...prevFormData.trafficInsurancePolicy,
                                                    trafficInsuranceNumber: value,
                                                },
                                            }))
                                        }}
                                        invalid={!!errors.trafficInsuranceNumber}
                                        disabled={clientTrafficAccess.disabled || clientTrafficIsNoInsurance}
                                        required={clientTrafficDetailsRequired}
                                    />
                                    <FormFeedback>{errors.trafficInsuranceNumber}</FormFeedback>
                                </Col>
                                <Col md={3}>
                                    <Label for="trafficInsuranceStart">Müvekkil Trafik Başlangıç {clientTrafficDetailsRequired && <span className="text-danger">*</span>}</Label>
                                    <Input
                                        type="date"
                                        id="trafficInsuranceStart"
                                        name="trafficInsuranceStart"
                                        value={
                                            formData.trafficInsurancePolicy?.trafficInsuranceStart instanceof Date && !isNaN(formData.trafficInsurancePolicy?.trafficInsuranceStart)
                                                ? formData.trafficInsurancePolicy.trafficInsuranceStart
                                                    .toISOString()
                                                    .substring(0, 10)
                                                : ''
                                        }
                                        onChange={(e) =>
                                            setFormData((prevFormData) => ({
                                                ...prevFormData,
                                                trafficInsurancePolicy: {
                                                    ...prevFormData.trafficInsurancePolicy,
                                                    trafficInsuranceStart: e.target.value
                                                        ? new Date(e.target.value)
                                                        : null,
                                                },
                                            }))
                                        }
                                        invalid={!!errors.trafficInsuranceStart}
                                        disabled={clientTrafficStartAccess.disabled || clientTrafficIsNoInsurance}
                                        required={clientTrafficDetailsRequired}
                                    />
                                    <FormFeedback>{errors.trafficInsuranceStart}</FormFeedback>
                                </Col>
                                <Col md={3}>
                                    <Label for="trafficInsuranceExpiry">Müvekkil Trafik Sigorta Vade {clientTrafficDetailsRequired && <span className="text-danger">*</span>}</Label>
                                    <Input
                                        type="date"
                                        id="trafficInsuranceExpiry"
                                        name="trafficInsuranceExpiry"
                                        value={
                                            formData.trafficInsurancePolicy?.trafficInsuranceExpiry instanceof Date && !isNaN(formData.trafficInsurancePolicy?.trafficInsuranceExpiry)
                                                ? formData.trafficInsurancePolicy.trafficInsuranceExpiry
                                                    .toISOString()
                                                    .substring(0, 10)
                                                : ''
                                        }
                                        onChange={(e) =>
                                            setFormData((prevFormData) => ({
                                                ...prevFormData,
                                                trafficInsurancePolicy: {
                                                    ...prevFormData.trafficInsurancePolicy,
                                                    trafficInsuranceExpiry: e.target.value
                                                        ? new Date(e.target.value)
                                                        : null,
                                                },
                                            }))
                                        }
                                        invalid={!!errors.trafficInsuranceExpiry}
                                        disabled={clientTrafficAccess.disabled || clientTrafficIsNoInsurance}
                                        required={clientTrafficDetailsRequired}
                                    />
                                    <FormFeedback>{errors.trafficInsuranceExpiry}</FormFeedback>
                                </Col>
                            </Row>
                        </FormGroup>

                        <FormGroup>
                            <Row>
                                <Col md={3}>
                                    <Label for="cascoInsuranceProvider">Müvekkil Kasko Sağlayıcısı {clientCascoAccess.required && <span className="text-danger">*</span>}</Label>
                                    <Input
                                        type="select"
                                        id="cascoInsuranceProvider"
                                        name="cascoInsuranceProvider"
                                        value={formData.cascoInsurancePolicy?.cascoInsuranceProvider?.[0] || ''}
                                        onChange={(e) => {
                                            const providerId = e.target.value;
                                            const isNoInsurance = isNoInsuranceProvider(providerId, insuranceCompanies);
                                            setFormData((prevFormData) => ({
                                                ...prevFormData,
                                                cascoInsurancePolicy: {
                                                    ...prevFormData.cascoInsurancePolicy,
                                                    cascoInsuranceProvider: providerId === "" ? [] : [providerId],
                                                    ...(isNoInsurance ? {
                                                        cascoInsuranceNumber: '',
                                                        cascoInsuranceStart: null,
                                                        cascoInsuranceExpiry: null,
                                                    } : {})
                                                },
                                            }))
                                        }}
                                        invalid={!!errors.cascoInsuranceProvider}
                                        disabled={clientCascoAccess.disabled}
                                        required={clientCascoAccess.required}
                                    >
                                        <option value="">Seçiniz</option>
                                        {insuranceCompanies && insuranceCompanies.map((company) => (
                                            <option key={company.id} value={company.id}>
                                                {company.name}
                                            </option>
                                        ))}
                                    </Input>
                                    <FormFeedback>{errors.cascoInsuranceProvider}</FormFeedback>
                                </Col>
                                <Col md={3}>
                                    <Label for="cascoInsuranceNumber">Müvekkil Kasko Numarası {clientCascoDetailsRequired && <span className="text-danger">*</span>}</Label>
                                    <Input
                                        type="text"
                                        id="cascoInsuranceNumber"
                                        name="cascoInsuranceNumber"
                                        value={formData.cascoInsurancePolicy?.cascoInsuranceNumber || ''}
                                        onChange={(e) => {
                                            const value = normalizeFieldValue('cascoInsuranceNumber', e.target.value);
                                            setFormData((prevFormData) => ({
                                                ...prevFormData,
                                                cascoInsurancePolicy: {
                                                    ...prevFormData.cascoInsurancePolicy,
                                                    cascoInsuranceNumber: value,
                                                },
                                            }))
                                        }}
                                        invalid={!!errors.cascoInsuranceNumber}
                                        disabled={clientCascoAccess.disabled || clientCascoIsNoInsurance}
                                        required={clientCascoDetailsRequired}
                                    />
                                    <FormFeedback>{errors.cascoInsuranceNumber}</FormFeedback>
                                </Col>
                                <Col md={3}>
                                    <Label for="cascoInsuranceStart">Müvekkil Kasko Başlangıç {clientCascoDetailsRequired && <span className="text-danger">*</span>}</Label>
                                    <Input
                                        type="date"
                                        id="cascoInsuranceStart"
                                        name="cascoInsuranceStart"
                                        value={
                                            formData.cascoInsurancePolicy?.cascoInsuranceStart instanceof Date && !isNaN(formData.cascoInsurancePolicy?.cascoInsuranceStart)
                                                ? formData.cascoInsurancePolicy.cascoInsuranceStart
                                                    .toISOString()
                                                    .substring(0, 10)
                                                : ''
                                        }
                                        onChange={(e) =>
                                            setFormData((prevFormData) => ({
                                                ...prevFormData,
                                                cascoInsurancePolicy: {
                                                    ...prevFormData.cascoInsurancePolicy,
                                                    cascoInsuranceStart: e.target.value
                                                        ? new Date(e.target.value)
                                                        : null,
                                                },
                                            }))
                                        }
                                        invalid={!!errors.cascoInsuranceStart}
                                        disabled={clientCascoStartAccess.disabled || clientCascoIsNoInsurance}
                                        required={clientCascoDetailsRequired}
                                    />
                                    <FormFeedback>{errors.cascoInsuranceStart}</FormFeedback>
                                </Col>
                                <Col md={3}>
                                    <Label for="cascoInsuranceExpiry">Müvekkil Kasko Vade {clientCascoDetailsRequired && <span className="text-danger">*</span>}</Label>
                                    <Input
                                        type="date"
                                        id="cascoInsuranceExpiry"
                                        name="cascoInsuranceExpiry"
                                        value={
                                            formData.cascoInsurancePolicy?.cascoInsuranceExpiry instanceof Date && !isNaN(formData.cascoInsurancePolicy?.cascoInsuranceExpiry)
                                                ? formData.cascoInsurancePolicy.cascoInsuranceExpiry
                                                    .toISOString()
                                                    .substring(0, 10)
                                                : ''
                                        }
                                        onChange={(e) =>
                                            setFormData((prevFormData) => ({
                                                ...prevFormData,
                                                cascoInsurancePolicy: {
                                                    ...prevFormData.cascoInsurancePolicy,
                                                    cascoInsuranceExpiry: e.target.value
                                                        ? new Date(e.target.value)
                                                        : null,
                                                },
                                            }))
                                        }
                                        invalid={!!errors.cascoInsuranceExpiry}
                                        disabled={clientCascoAccess.disabled || clientCascoIsNoInsurance}
                                        required={clientCascoDetailsRequired}
                                    />
                                    <FormFeedback>{errors.cascoInsuranceExpiry}</FormFeedback>
                                </Col>
                            </Row>
                        </FormGroup>

                        <div style={{ height: "20px" }}></div>
                        <H6>Karşı Taraf Sigorta Bilgileri</H6>
                        <div style={{ height: "20px" }}></div>

                        <FormGroup>
                            <Label for="counterpartyLicensePlateNumber">Karşı Taraf Plaka {counterpartyPlateAccess.required && <span className="text-danger">*</span>}</Label>
                            <Input
                                type="text"
                                id="counterpartyLicensePlateNumber"
                                name="counterpartyLicensePlateNumber"
                                value={formData.counterpartyLicensePlateNumber || ''}
                                onChange={(e) => {
                                    const value = normalizeFieldValue('counterpartyLicensePlateNumber', e.target.value);
                                    handleInputChange({
                                        target: {
                                            name: 'counterpartyLicensePlateNumber',
                                            value
                                        }
                                    });
                                }}
                                placeholder="Örn: 34 AB 123"
                                invalid={!!errors.counterpartyLicensePlateNumber || !!(formData.counterpartyLicensePlateNumber && !validateLicensePlate(formData.counterpartyLicensePlateNumber))}
                                disabled={counterpartyPlateAccess.disabled}
                                required={counterpartyPlateAccess.required}
                            />
                            <FormFeedback>{errors.counterpartyLicensePlateNumber || (formData.counterpartyLicensePlateNumber && !validateLicensePlate(formData.counterpartyLicensePlateNumber) ? 'Geçerli bir plaka giriniz (Örn: 34 AB 123).' : '')}</FormFeedback>
                        </FormGroup>

                        <FormGroup>
                            <Label for="counterpartyFaultRate">Karşı Taraf Kusur Oranı {counterpartyFaultRateAccess.required && <span className="text-danger">*</span>}</Label>
                            <div className="input-group">
                                <Input
                                    type="text"
                                    id="counterpartyFaultRate"
                                    name="counterpartyFaultRate"
                                    value={displayDecimal(formData.counterpartyFaultRate || '')}
                                    readOnly={true}
                                    placeholder="Otomatik hesaplanır"
                                    invalid={!!errors.counterpartyFaultRate}
                                    disabled={counterpartyFaultRateAccess.disabled}
                                    required={counterpartyFaultRateAccess.required}
                                />
                                <div className="input-group-append">
                                    <span className="input-group-text">%</span>
                                </div>
                                <FormFeedback>
                                    {errors.counterpartyFaultRate}
                                </FormFeedback>
                            </div>
                        </FormGroup>

                        <FormGroup>
                            <Row>
                                <Col md={3}>
                                    <Label for="counterpartyTrafficInsuranceProvider">Karşı Taraf Trafik Sigorta Sağlayıcısı {cpTrafficAccess.required && <span className="text-danger">*</span>}</Label>
                                    <Input
                                        type="select"
                                        id="counterpartyTrafficInsuranceProvider"
                                        name="counterpartyTrafficInsuranceProvider"
                                        value={formData.counterpartyTrafficInsurancePolicy?.trafficInsuranceProvider?.[0] || ''}
                                        onChange={(e) => {
                                            const providerId = e.target.value;
                                            const isNoInsurance = isNoInsuranceProvider(providerId, insuranceCompanies);
                                            setFormData((prevFormData) => ({
                                                ...prevFormData,
                                                counterpartyTrafficInsurancePolicy: {
                                                    ...prevFormData.counterpartyTrafficInsurancePolicy,
                                                    trafficInsuranceProvider: providerId === "" ? [] : [providerId],
                                                    ...(isNoInsurance ? {
                                                        trafficInsuranceNumber: '',
                                                        trafficInsuranceStart: null,
                                                        trafficInsuranceExpiry: null,
                                                    } : {})
                                                },
                                            }))
                                        }}
                                        invalid={!!errors.counterpartyTrafficInsuranceProvider}
                                        disabled={cpTrafficAccess.disabled}
                                        required={cpTrafficAccess.required}
                                    >
                                        <option value="">Seçiniz</option>
                                        {insuranceCompanies && insuranceCompanies.map((company) => (
                                            <option key={company.id} value={company.id}>
                                                {company.name}
                                            </option>
                                        ))}
                                    </Input>
                                    <FormFeedback>{errors.counterpartyTrafficInsuranceProvider}</FormFeedback>
                                </Col>
                                <Col md={3}>
                                    <Label for="counterpartyTrafficInsuranceNumber">Karşı Taraf Trafik Sigorta Numarası {cpTrafficDetailsRequired && <span className="text-danger">*</span>}</Label>
                                    <Input
                                        type="text"
                                        id="counterpartyTrafficInsuranceNumber"
                                        name="counterpartyTrafficInsuranceNumber"
                                        value={formData.counterpartyTrafficInsurancePolicy?.trafficInsuranceNumber || ''}
                                        onChange={(e) => {
                                            const value = normalizeFieldValue('counterpartyTrafficInsuranceNumber', e.target.value);
                                            setFormData((prevFormData) => ({
                                                ...prevFormData,
                                                counterpartyTrafficInsurancePolicy: {
                                                    ...prevFormData.counterpartyTrafficInsurancePolicy,
                                                    trafficInsuranceNumber: value,
                                                },
                                            }))
                                        }}
                                        invalid={!!errors.counterpartyTrafficInsuranceNumber}
                                        disabled={cpTrafficAccess.disabled || cpTrafficIsNoInsurance}
                                        required={cpTrafficDetailsRequired}
                                    />
                                    <FormFeedback>{errors.counterpartyTrafficInsuranceNumber}</FormFeedback>
                                </Col>
                                <Col md={3}>
                                    <Label for="counterpartyTrafficInsuranceStart">Karşı Taraf Trafik Başlangıç {cpTrafficDetailsRequired && <span className="text-danger">*</span>}</Label>
                                    <Input
                                        type="date"
                                        id="counterpartyTrafficInsuranceStart"
                                        name="counterpartyTrafficInsuranceStart"
                                        value={
                                            formData.counterpartyTrafficInsurancePolicy?.trafficInsuranceStart instanceof Date && !isNaN(formData.counterpartyTrafficInsurancePolicy?.trafficInsuranceStart)
                                                ? formData.counterpartyTrafficInsurancePolicy.trafficInsuranceStart
                                                    .toISOString()
                                                    .substring(0, 10)
                                                : ''
                                        }
                                        onChange={(e) =>
                                            setFormData((prevFormData) => ({
                                                ...prevFormData,
                                                counterpartyTrafficInsurancePolicy: {
                                                    ...prevFormData.counterpartyTrafficInsurancePolicy,
                                                    trafficInsuranceStart: e.target.value
                                                        ? new Date(e.target.value)
                                                        : null,
                                                },
                                            }))
                                        }
                                        invalid={!!errors.counterpartyTrafficInsuranceStart}
                                        disabled={cpTrafficStartAccess.disabled || cpTrafficIsNoInsurance}
                                        required={cpTrafficDetailsRequired}
                                    />
                                    <FormFeedback>{errors.counterpartyTrafficInsuranceStart}</FormFeedback>
                                </Col>
                                <Col md={3}>
                                    <Label for="counterpartyTrafficInsuranceExpiry">Karşı Taraf Trafik Sigorta Vade {cpTrafficDetailsRequired && <span className="text-danger">*</span>}</Label>
                                    <Input
                                        type="date"
                                        id="counterpartyTrafficInsuranceExpiry"
                                        name="counterpartyTrafficInsuranceExpiry"
                                        value={
                                            formData.counterpartyTrafficInsurancePolicy?.trafficInsuranceExpiry instanceof Date && !isNaN(formData.counterpartyTrafficInsurancePolicy?.trafficInsuranceExpiry)
                                                ? formData.counterpartyTrafficInsurancePolicy.trafficInsuranceExpiry
                                                    .toISOString()
                                                    .substring(0, 10)
                                                : ''
                                        }
                                        onChange={(e) =>
                                            setFormData((prevFormData) => ({
                                                ...prevFormData,
                                                counterpartyTrafficInsurancePolicy: {
                                                    ...prevFormData.counterpartyTrafficInsurancePolicy,
                                                    trafficInsuranceExpiry: e.target.value
                                                        ? new Date(e.target.value)
                                                        : null,
                                                },
                                            }))
                                        }
                                        invalid={!!errors.counterpartyTrafficInsuranceExpiry}
                                        disabled={cpTrafficAccess.disabled || cpTrafficIsNoInsurance}
                                        required={cpTrafficDetailsRequired}
                                    />
                                    <FormFeedback>{errors.counterpartyTrafficInsuranceExpiry}</FormFeedback>
                                </Col>
                            </Row>
                        </FormGroup>

                        <FormGroup>
                            <Row>
                                <Col md={3}>
                                    <Label for="counterpartyCascoInsuranceProvider">Karşı Taraf Kasko Sağlayıcısı {cpCascoAccess.required && <span className="text-danger">*</span>}</Label>
                                    <Input
                                        type="select"
                                        id="counterpartyCascoInsuranceProvider"
                                        name="counterpartyCascoInsuranceProvider"
                                        value={formData.counterpartyCascoInsurancePolicy?.cascoInsuranceProvider?.[0] || ''}
                                        onChange={(e) => {
                                            const providerId = e.target.value;
                                            const isNoInsurance = isNoInsuranceProvider(providerId, insuranceCompanies);
                                            setFormData((prevFormData) => ({
                                                ...prevFormData,
                                                counterpartyCascoInsurancePolicy: {
                                                    ...prevFormData.counterpartyCascoInsurancePolicy,
                                                    cascoInsuranceProvider: providerId === "" ? [] : [providerId],
                                                    ...(isNoInsurance ? {
                                                        cascoInsuranceNumber: '',
                                                        cascoInsuranceStart: null,
                                                        cascoInsuranceExpiry: null,
                                                    } : {})
                                                },
                                            }))
                                        }}
                                        invalid={!!errors.counterpartyCascoInsuranceProvider}
                                        disabled={cpCascoAccess.disabled}
                                        required={cpCascoAccess.required}
                                    >
                                        <option value="">Seçiniz</option>
                                        {insuranceCompanies && insuranceCompanies.map((company) => (
                                            <option key={company.id} value={company.id}>
                                                {company.name}
                                            </option>
                                        ))}
                                    </Input>
                                    <FormFeedback>{errors.counterpartyCascoInsuranceProvider}</FormFeedback>
                                </Col>
                                <Col md={3}>
                                    <Label for="counterpartyCascoInsuranceNumber">Karşı Taraf Kasko Numarası {cpCascoDetailsRequired && <span className="text-danger">*</span>}</Label>
                                    <Input
                                        type="text"
                                        id="counterpartyCascoInsuranceNumber"
                                        name="counterpartyCascoInsuranceNumber"
                                        value={formData.counterpartyCascoInsurancePolicy?.cascoInsuranceNumber || ''}
                                        onChange={(e) => {
                                            const value = normalizeFieldValue('counterpartyCascoInsuranceNumber', e.target.value);
                                            setFormData((prevFormData) => ({
                                                ...prevFormData,
                                                counterpartyCascoInsurancePolicy: {
                                                    ...prevFormData.counterpartyCascoInsurancePolicy,
                                                    cascoInsuranceNumber: value,
                                                },
                                            }))
                                        }}
                                        invalid={!!errors.counterpartyCascoInsuranceNumber}
                                        disabled={cpCascoAccess.disabled || cpCascoIsNoInsurance}
                                        required={cpCascoDetailsRequired}
                                    />
                                    <FormFeedback>{errors.counterpartyCascoInsuranceNumber}</FormFeedback>
                                </Col>
                                <Col md={3}>
                                    <Label for="counterpartyCascoInsuranceStart">Karşı Taraf Kasko Başlangıç {cpCascoDetailsRequired && <span className="text-danger">*</span>}</Label>
                                    <Input
                                        type="date"
                                        id="counterpartyCascoInsuranceStart"
                                        name="counterpartyCascoInsuranceStart"
                                        value={
                                            formData.counterpartyCascoInsurancePolicy?.cascoInsuranceStart instanceof Date && !isNaN(formData.counterpartyCascoInsurancePolicy?.cascoInsuranceStart)
                                                ? formData.counterpartyCascoInsurancePolicy.cascoInsuranceStart
                                                    .toISOString()
                                                    .substring(0, 10)
                                                : ''
                                        }
                                        onChange={(e) =>
                                            setFormData((prevFormData) => ({
                                                ...prevFormData,
                                                counterpartyCascoInsurancePolicy: {
                                                    ...prevFormData.counterpartyCascoInsurancePolicy,
                                                    cascoInsuranceStart: e.target.value
                                                        ? new Date(e.target.value)
                                                        : null,
                                                },
                                            }))
                                        }
                                        invalid={!!errors.counterpartyCascoInsuranceStart}
                                        disabled={cpCascoStartAccess.disabled || cpCascoIsNoInsurance}
                                        required={cpCascoDetailsRequired}
                                    />
                                    <FormFeedback>{errors.counterpartyCascoInsuranceStart}</FormFeedback>
                                </Col>
                                <Col md={3}>
                                    <Label for="counterpartyCascoInsuranceExpiry">Karşı Taraf Kasko Vade {cpCascoDetailsRequired && <span className="text-danger">*</span>}</Label>
                                    <Input
                                        type="date"
                                        id="counterpartyCascoInsuranceExpiry"
                                        name="counterpartyCascoInsuranceExpiry"
                                        value={
                                            formData.counterpartyCascoInsurancePolicy?.cascoInsuranceExpiry instanceof Date && !isNaN(formData.counterpartyCascoInsurancePolicy?.cascoInsuranceExpiry)
                                                ? formData.counterpartyCascoInsurancePolicy.cascoInsuranceExpiry
                                                    .toISOString()
                                                    .substring(0, 10)
                                                : ''
                                        }
                                        onChange={(e) =>
                                            setFormData((prevFormData) => ({
                                                ...prevFormData,
                                                counterpartyCascoInsurancePolicy: {
                                                    ...prevFormData.counterpartyCascoInsurancePolicy,
                                                    cascoInsuranceExpiry: e.target.value
                                                        ? new Date(e.target.value)
                                                        : null,
                                                },
                                            }))
                                        }
                                        invalid={!!errors.counterpartyCascoInsuranceExpiry}
                                        disabled={cpCascoAccess.disabled || cpCascoIsNoInsurance}
                                        required={cpCascoDetailsRequired}
                                    />
                                    <FormFeedback>{errors.counterpartyCascoInsuranceExpiry}</FormFeedback>
                                </Col>
                            </Row>
                        </FormGroup>

                        <div style={{ height: "20px" }}></div>
                        <hr className="mt-4 mb-4" />
                        <div style={{ height: "20px" }}></div>
                    </>
                )}

            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                <H5 className="mb-0">Çalışılacak Dosyalar</H5>
                {showLorShortcutButton && (
                    <button
                        type="button"
                        className="btn btn-outline-primary d-inline-flex align-items-center"
                        onClick={() => setIsLorOnlyModalOpen(true)}
                        style={{ height: '35px' }}
                    >
                        <PlusCircle size={16} className="me-1" />
                        Hak Mahrumiyeti Ekle
                    </button>
                )}
            </div>

            <AddLorOnlyFileModal
                isOpen={isLorOnlyModalOpen}
                toggle={() => setIsLorOnlyModalOpen(false)}
                callFormId={callFormId}
                onSuccess={handleLorCreated}
            />

            {callFormState === 'İcra Sürecine Yönlendirildi' && (
                <Alert color="info" className="mt-3 mb-3">
                    <h6 className="alert-heading mb-2"><i className="fa fa-info-circle me-2"></i>İcra Sürecine Yönlendirildi</h6>
                    <p className="mb-0">
                        Bu çağrı formundaki tüm kaza dosyaları <strong>Hak Mahrumiyeti</strong> türündedir.
                        Eksper raporu, eksper raporu ödemesi ve sigorta başvuru dilekçesi süreçleri atlanmıştır.
                        Dosyalar doğrudan <strong>İcra Başvuru Bekleniyor</strong> durumuna yönlendirilmiştir.
                    </p>
                </Alert>
            )}

            {['Eksper İşlem Bekleniyor',
                'Yeni Eksper İşlem Bekleniyor',
                'Tüm Form'].includes(callFormState) && (
                    <>
                        <div style={{ height: "20px" }}></div>

                        <h6 className="mb-3">Yeni Dosya Ekle</h6>

                        {(() => {
                            return (
                                <>
                                    {formData.willLorBeWorked === "no" && (
                                        <div className="alert alert-warning mb-3">
                                            <strong>Bilgi:</strong> Hak mahrumiyeti çalışılmayacak olarak işaretlendiği için,
                                            hak mahrumiyeti ile ilgili dosyalar oluşturulamayacak ve raporlanamayacaktır.
                                        </div>
                                    )}

                                    <Row className="mb-3">
                                        <Col md={6}>
                                            <FormGroup>
                                                <Label className="form-label">Dosya Türü (Maximum 2 seçim) {fileTypeAccess.required && <span className="text-danger">*</span>}</Label>
                                                <div className="file-type-checkboxes border p-3 rounded" style={{ maxHeight: '250px', overflowY: 'auto' }}>
                                                    {workFileTypes && workFileTypes.length > 0 ?
                                                        workFileTypes
                                                            .filter(fileType => {
                                                                const isLorFileTypeCheck = fileType.title.toLowerCase().includes('hak mahrumiyeti') ||
                                                                    fileType.title.toLowerCase().includes('lor') ||
                                                                    fileType.title.toLowerCase().includes('mahrumiyeti');

                                                                return !(isLorFileTypeCheck && formData.willLorBeWorked === "no");
                                                            })
                                                            .map((fileType) => (
                                                                <div key={fileType.id} className="checkbox-item mb-2">
                                                                    <div className="checkbox checkbox-primary d-flex align-items-center">
                                                                        <Input
                                                                            type="checkbox"
                                                                            id={`fileType-${fileType.id}`}
                                                                            checked={newFile.fileType.includes(fileType.id)}
                                                                            onChange={(e) => handleFileTypeChange(fileType.id, e.target.checked)}
                                                                            disabled={fileTypeAccess.disabled || (isLorFileType(fileType.id) && hasNonLorSelected) || (!isLorFileType(fileType.id) && hasLorSelected)}
                                                                        />
                                                                        <Label htmlFor={`fileType-${fileType.id}`} className="ms-2 mb-0">
                                                                            {fileType.title}
                                                                        </Label>
                                                                    </div>
                                                                </div>
                                                            )) :
                                                        <div className="text-center text-muted">Dosya türü bulunamadı</div>
                                                    }
                                                </div>
                                                {newFileErrors.fileType && <div className="text-danger small mt-1">{newFileErrors.fileType}</div>}
                                            </FormGroup>
                                        </Col>

                                        {newFile.fileType.length > 0 && (
                                            <Col md={6}>
                                                <Label className="form-label">Dosya Özellikleri</Label>
                                                <div className="d-flex flex-column">
                                                    <FormGroup check inline className="checkbox checkbox-success me-3 mb-2">
                                                        <Input
                                                            type="checkbox"
                                                            id="withReport"
                                                            checked={newFile.withReport}
                                                            onChange={() => handleCheckboxChange('withReport')}
                                                            disabled={withReportAccess.disabled}
                                                        />
                                                        <Label for="withReport" check className="text-success fw-medium">Raporlu</Label>
                                                    </FormGroup>
                                                    <FormGroup check inline className="checkbox checkbox-warning me-3 mb-2">
                                                        <Input
                                                            type="checkbox"
                                                            id="withFault"
                                                            checked={newFile.withFault}
                                                            onChange={() => handleCheckboxChange('withFault')}
                                                            disabled={withFaultAccess.disabled}
                                                        />
                                                        <Label for="withFault" check className="text-warning fw-medium">Kusurlu</Label>
                                                    </FormGroup>
                                                    <FormGroup check inline className="checkbox checkbox-danger">
                                                        <Input
                                                            type="checkbox"
                                                            id="withoutReport"
                                                            checked={newFile.withoutReport}
                                                            onChange={() => handleCheckboxChange('withoutReport')}
                                                            disabled={withoutReportAccess.disabled}
                                                        />
                                                        <Label for="withoutReport" check className="text-danger fw-medium">Raporsuz</Label>
                                                    </FormGroup>
                                                </div>
                                            </Col>
                                        )}
                                    </Row>

                                    {newFile.fileType.length > 0 && (
                                        <Row className="mb-3">
                                            <Col md={12}>
                                                <Label className="form-label mb-3">Dosya Türü Tutarları</Label>
                                                <Row>
                                                    {newFile.fileType.map((typeId, index) => {
                                                        const fileType = workFileTypes.find(type => type.id === typeId);
                                                        const isLor = isLorFileType(typeId);
                                                        if (isLor) {
                                                            const calculatedExpense = (parseInt(newFile.numberOfDays || 0) * parseFloat(newFile.expensePerDay || 0));
                                                            return (
                                                                <Col md={12} key={typeId}>
                                                                    <Label className="form-label mb-3">{fileType ? fileType.title : 'Hak Mahrumiyeti'}</Label>
                                                                    <Row>
                                                                        <Col md={4}>
                                                                            <FormGroup>
                                                                                <Label for="numberOfDays" className="form-label">Gün Sayısı {numberOfDaysAccess.required && <span className="text-danger">*</span>}</Label>
                                                                                <Input
                                                                                    type="text"
                                                                                    id="numberOfDays"
                                                                                    name="numberOfDays"
                                                                                    value={newFile.numberOfDays}
                                                                                    onChange={(e) => {
                                                                                        const value = normalizeDigitsOnly(e.target.value);
                                                                                        handleChange('numberOfDays', value);
                                                                                    }}
                                                                                    placeholder="0"
                                                                                    invalid={!!(errors.numberOfDays || newFileErrors.numberOfDays)}
                                                                                    className="form-control"
                                                                                    disabled={numberOfDaysAccess.disabled}
                                                                                    required={numberOfDaysAccess.required}
                                                                                />
                                                                                <FormFeedback>{errors.numberOfDays || newFileErrors.numberOfDays}</FormFeedback>
                                                                            </FormGroup>
                                                                        </Col>
                                                                        <Col md={4}>
                                                                            <FormGroup>
                                                                                <Label for="expensePerDay" className="form-label">Gün Başı Fiyat {expensePerDayAccess.required && <span className="text-danger">*</span>}</Label>
                                                                                <Input
                                                                                    type="text"
                                                                                    id="expensePerDay"
                                                                                    name="expensePerDay"
                                                                                    value={displayDecimal(newFile.expensePerDay)}
                                                                                    onChange={(e) => {
                                                                                        const value = normalizeDecimal(e.target.value);
                                                                                        handleChange('expensePerDay', value);
                                                                                    }}
                                                                                    placeholder="Örn: 1000,50"
                                                                                    invalid={!!(errors.expensePerDay || newFileErrors.expensePerDay)}
                                                                                    className="form-control"
                                                                                    disabled={expensePerDayAccess.disabled}
                                                                                    required={expensePerDayAccess.required}
                                                                                />
                                                                                <FormFeedback>{errors.expensePerDay || newFileErrors.expensePerDay}</FormFeedback>
                                                                            </FormGroup>
                                                                        </Col>
                                                                        <Col md={4}>
                                                                            <FormGroup>
                                                                                <Label className="form-label">Toplam Tutar (Otomatik Hesaplanan)</Label>
                                                                                <Input
                                                                                    type="text"
                                                                                    value={calculatedExpense.toLocaleString('tr-TR')}
                                                                                    disabled={true}
                                                                                    className="form-control text-dark fw-bold"
                                                                                    style={{ backgroundColor: '#e9ecef' }}
                                                                                />
                                                                            </FormGroup>
                                                                        </Col>
                                                                    </Row>
                                                                </Col>
                                                            );
                                                        } else {
                                                            return (
                                                                <Col md={6} key={typeId}>
                                                                    <FormGroup>
                                                                        <Label className="form-label">
                                                                            {fileType ? fileType.title : 'Bilinmeyen'} Tutarı
                                                                            {fileExpenseAccess.required && <span className="text-danger">*</span>}
                                                                        </Label>
                                                                        <Input
                                                                            type="text"
                                                                            value={displayDecimal(newFile.fileTypeExpenses[typeId] || '')}
                                                                            onChange={(e) => {
                                                                                const value = normalizeDecimal(e.target.value);
                                                                                handleFileTypeExpenseChange(typeId, value);
                                                                            }}
                                                                            placeholder="Örn: 1000,50"
                                                                            className="form-control"
                                                                            disabled={fileExpenseAccess.disabled}
                                                                            required={fileExpenseAccess.required}
                                                                            invalid={!!newFileErrors.fileTypeExpenses?.[typeId]}
                                                                        />
                                                                        <FormFeedback>{newFileErrors.fileTypeExpenses?.[typeId]}</FormFeedback>
                                                                    </FormGroup>
                                                                </Col>
                                                            );
                                                        }
                                                    })}
                                                </Row>
                                            </Col>
                                        </Row>
                                    )}

                                    <Row className="mt-4 mb-4">
                                        <Col md={12} className="text-end">
                                            <Button
                                                color="primary"
                                                onClick={handleAdd}
                                                disabled={addWorkFileAccess.disabled}
                                            >
                                                Dosya Ekle
                                            </Button>
                                        </Col>
                                    </Row>

                                    <hr />
                                </>
                            );
                        })()}
                    </>
                )}

            <div className="mt-4">
                <h6 className="mb-3">Eklenen Dosyalar</h6>
                {(errors.expertReportFiles || errors.insuranceApplicationFiles) && (
                    <Alert color="danger" className="py-2">
                        {errors.expertReportFiles || errors.insuranceApplicationFiles}
                    </Alert>
                )}

                {formData.filesToBeWorked && formData.filesToBeWorked.length > 0 ? (
                    <div className="table-responsive">
                        <table className="table table-bordered table-striped">
                            <thead className="bg-light">
                                <tr>
                                    {['Eksper İşlem Bekleniyor', 'Yeni Eksper İşlem Bekleniyor', 'Tüm Form'].includes(callFormState) && (
                                        <th style={{ width: '80px' }}>İşlem</th>
                                    )}
                                    <th style={{ minWidth: '100px' }}>Dosya No</th>
                                    <th style={{ minWidth: '150px' }}>Dosya Türü</th>
                                    <th style={{ minWidth: '150px' }}>Tutar Bilgileri</th>
                                    <th style={{ minWidth: '120px' }}>Özellikler</th>
                                    {['Eksper Rapor Bekleniyor', 'Yeni Eksper Rapor Bekleniyor', 'Eksper Raporu Ödeme Bekleniyor', 'Yeni Eksper Raporu Ödeme Bekleniyor'].includes(callFormState) && (
                                        <th style={{ minWidth: '300px' }}>Eksper Rapor</th>
                                    )}
                                    {['Sigorta Başvuru Dilekçe Bekleniyor', 'Yeni Sigorta Başvuru Dilekçe Bekleniyor', 'Ekspersiz Yeni Sigorta Başvuru Dilekçe Bekleniyor'].includes(callFormState) && (
                                        <th style={{ minWidth: '300px' }}>Sigorta Başvuru Dilekçesi</th>
                                    )}
                                </tr>
                            </thead>
                            <tbody>
                                {(() => {
                                    return formData.filesToBeWorked.map((accidentFileEntry, index) => {
                                        const matchedAccidentFile = accidentFileEntry.accidentFileId
                                            ? formData.accidentFiles?.find(af => af.id === accidentFileEntry.accidentFileId)
                                            : null;
                                        const fileNumber = accidentFileEntry.fileNumber || (
                                            matchedAccidentFile ? matchedAccidentFile.fileNumber : (accidentFileEntry.accidentFileId || 'Yeni')
                                        );

                                        let actualEntry = accidentFileEntry;
                                        let allFileTypeTitles = [];
                                        
                                        if (accidentFileEntry.fileType && !accidentFileEntry.filesToBeWorked) {
                                            allFileTypeTitles = accidentFileEntry._fileTypeTitles || [];
                                            actualEntry = {
                                                ...accidentFileEntry,
                                                filesToBeWorked: [accidentFileEntry]
                                            };
                                        } else {
                                            actualEntry.filesToBeWorked?.forEach(file => {
                                                if (file._fileTypeTitles && Array.isArray(file._fileTypeTitles)) {
                                                    allFileTypeTitles.push(...file._fileTypeTitles);
                                                }
                                            });
                                        }

                                        const lorTitles = allFileTypeTitles.filter(title => {
                                            return title.toLowerCase().includes('hak mahrumiyeti') ||
                                                title.toLowerCase().includes('lor') ||
                                                title.toLowerCase().includes('mahrumiyeti');
                                        });

                                        const nonLorTitles = allFileTypeTitles.filter(title => {
                                            return !(title.toLowerCase().includes('hak mahrumiyeti') ||
                                                title.toLowerCase().includes('lor') ||
                                                title.toLowerCase().includes('mahrumiyeti'));
                                        });

                                        const reorderedTitles = [...nonLorTitles, ...lorTitles];

                                        const isLorOnlyEntry = allFileTypeTitles.length > 0 && allFileTypeTitles.every(title =>
                                            title.toLowerCase().includes('hak mahrumiyeti') ||
                                            title.toLowerCase().includes('lor') ||
                                            title.toLowerCase().includes('mahrumiyeti'));

                                        const isReapplicationFile = !actualEntry.accidentFileId || accidentFileEntry.isReapplicationFile === true || actualEntry.isReapplicationFile === true || matchedAccidentFile?.isReapplicationFile === true;
                                        const isFromInsurance = accidentFileEntry.isFromInsurance === true || actualEntry.isFromInsurance === true || matchedAccidentFile?.isFromInsurance === true;

                                        let entryCanEdit = canEdit;
                                        if (isInsuranceOnlyWorkflow) {
                                            entryCanEdit = entryCanEdit && isFromInsurance;
                                        } else if (requiresReapplicationFlag) {
                                            entryCanEdit = entryCanEdit && isReapplicationFile;
                                        }

                                        const showDeleteButton = entryCanEdit && (['Eksper İşlem Bekleniyor', 'Tüm Form'].includes(callFormState) || 
                                            (callFormState === 'Yeni Eksper İşlem Bekleniyor' && !actualEntry.accidentFileId));
                                        
                                        const showCreateButton = entryCanEdit && callFormState === 'Yeni Eksper İşlem Bekleniyor' && !actualEntry.accidentFileId;
                                        const isCreating = creatingAccidentFileIndex === index;

                                        return (
                                            <tr key={`accident-file-${index}`} className="align-middle">
                                                {['Eksper İşlem Bekleniyor', 'Yeni Eksper İşlem Bekleniyor', 'Tüm Form'].includes(callFormState) && (
                                                    <td>
                                                        <div className="d-flex flex-column gap-1">
                                                            {showDeleteButton && (
                                                                <Button
                                                                    color="danger"
                                                                    size="sm"
                                                                    onClick={() => handleRemoveWorkFile(index)}
                                                                    disabled={removeWorkFileAccess.disabled || isCreating || !entryCanEdit}
                                                                >
                                                                    Sil
                                                                </Button>
                                                            )}
                                                            {showCreateButton && (
                                                                <Button
                                                                    color="success"
                                                                    size="sm"
                                                                    onClick={() => handleCreateAccidentFile(index)}
                                                                    disabled={isCreating || !entryCanEdit}
                                                                    title="Bu dosyayı yeni bir kaza dosyası olarak oluşturur"
                                                                >
                                                                    {isCreating ? (
                                                                        <>
                                                                            <Spinner size="sm" className="me-1" />
                                                                            Oluşturuluyor...
                                                                        </>
                                                                    ) : (
                                                                        'Oluştur'
                                                                    )}
                                                                </Button>
                                                            )}
                                                        </div>
                                                    </td>
                                                )}
                                                <td className="text-center">
                                                    <div className="d-flex flex-wrap justify-content-center align-items-center gap-1">
                                                        <span className={`badge ${actualEntry.accidentFileId ? 'bg-primary' : 'bg-success'} rounded-pill`}>
                                                            {fileNumber}
                                                        </span>
                                                        {isReapplicationFile && actualEntry.accidentFileId && (
                                                            <span className="badge bg-success rounded-pill">Yeni</span>
                                                        )}
                                                        {isLorOnlyEntry && actualEntry.accidentFileId && (
                                                            <span className="badge bg-info rounded-pill">İcra</span>
                                                        )}
                                                    </div>
                                                    {isLorOnlyEntry && callFormState !== 'Eksper İşlem Bekleniyor' && callFormState !== 'Yeni Eksper İşlem Bekleniyor' && callFormState !== 'İcra Sürecine Yönlendirildi' && callFormState !== 'Tüm Form' && (
                                                        <small className="text-info d-block mt-2">
                                                            <i className="fa fa-info-circle me-1"></i>
                                                            İcra sürecine yönlendirildi
                                                        </small>
                                                    )}
                                                </td>
                                                <td>
                                                    {reorderedTitles.length > 0 ?
                                                        reorderedTitles.join(', ') :
                                                        'N/A'}
                                                </td>
                                                <td>
                                                    <div>
                                                        {actualEntry.filesToBeWorked?.map((file, fileIndex) => {
                                                            const titles = file._fileTypeTitles || [];
                                                            return titles.map((title, titleIndex) => {
                                                                const isLorType = title.toLowerCase().includes('hak mahrumiyeti') ||
                                                                                 title.toLowerCase().includes('lor') ||
                                                                                 title.toLowerCase().includes('mahrumiyeti');
                                                                
                                                                const uniqueKey = `${fileIndex}-${titleIndex}`;
                                                                const isFirstFile = fileIndex === 0;
                                                                
                                                                let fileTypeAmount = 0;
                                                                if (isLorType) {
                                                                    fileTypeAmount = (file.numberOfDays || 0) * (file.expensePerDay || 0);
                                                                } else {
                                                                    if (file.fileTypeExpenseDetails) {
                                                                        const fileTypeId = file.fileType?.find(id => {
                                                                            const workFileType = workFileTypes.find(type => type.id === id);
                                                                            return workFileType && workFileType.title === title;
                                                                        });
                                                                        fileTypeAmount = file.fileTypeExpenseDetails[fileTypeId] || 0;
                                                                    } else {
                                                                        fileTypeAmount = file.fileExpense || 0;
                                                                    }
                                                                }
                                                                
                                                                return (
                                                                    <div key={uniqueKey} className={!isFirstFile ? "mt-3 pt-2 border-top" : ""}>
                                                                        <div className="fw-bold">{title}:</div>
                                                                        {isLorType ? (
                                                                            <>
                                                                                <div>Gün: {file.numberOfDays || 0}</div>
                                                                                <div>Gün Başı: {(file.expensePerDay || 0).toLocaleString('tr-TR')}</div>
                                                                                <div>Toplam: {fileTypeAmount.toLocaleString('tr-TR')}</div>
                                                                            </>
                                                                        ) : (
                                                                            <div>Tutar: {fileTypeAmount.toLocaleString('tr-TR')}</div>
                                                                        )}
                                                                    </div>
                                                                );
                                                            });
                                                        }).flat()}
                                                        {(!actualEntry.filesToBeWorked || actualEntry.filesToBeWorked.length === 0) && (
                                                            <div className="text-muted">Dosya türü belirtilmemiş</div>
                                                        )}
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="d-flex flex-wrap gap-2">
                                                        {actualEntry.withReport && (
                                                            <span className="badge bg-success rounded-pill" style={{ minWidth: '75px', textAlign: 'center' }}>Raporlu</span>
                                                        )}
                                                        {actualEntry.withFault && (
                                                            <span className="badge bg-warning rounded-pill" style={{ minWidth: '75px', textAlign: 'center' }}>Kusurlu</span>
                                                        )}
                                                        {actualEntry.withoutReport && (
                                                            <span className="badge bg-info rounded-pill" style={{ minWidth: '75px', textAlign: 'center' }}>Raporsuz</span>
                                                        )}
                                                    </div>
                                                </td>
                                                {['Eksper Rapor Bekleniyor', 'Yeni Eksper Rapor Bekleniyor', 'Eksper Raporu Ödeme Bekleniyor', 'Yeni Eksper Raporu Ödeme Bekleniyor'].includes(callFormState) && !isLorOnlyEntry && (
                                                    <td>
                                                        <div className="d-flex flex-column gap-2">
                                                            <FilesComponent
                                                                key={`expert-report-${actualEntry.accidentFileId || index}`}
                                                                onFilesChange={(files) => handleExpertReportFilesChange(actualEntry.accidentFileId || index, files)}
                                                                multiple={false}
                                                                callFormReference={false}
                                                                referenceId={actualEntry.accidentFileId || index}
                                                                typeFilter={['EKSPER RAPORU']}
                                                                title=""
                                                                canEdit={entryCanEdit}
                                                            />
                                                        </div>
                                                    </td>
                                                )}
                                                {['Sigorta Başvuru Dilekçe Bekleniyor', 'Yeni Sigorta Başvuru Dilekçe Bekleniyor', 'Ekspersiz Yeni Sigorta Başvuru Dilekçe Bekleniyor'].includes(callFormState) && !isLorOnlyEntry && (
                                                    <td>
                                                        <div className="d-flex flex-column gap-2">
                                                            <FilesComponent
                                                                key={`insurance-application-${actualEntry.accidentFileId || index}`}
                                                                onFilesChange={(files) => handleInsuranceApplicationFilesChange(actualEntry.accidentFileId || index, files)}
                                                                multiple={false}
                                                                callFormReference={false}
                                                                referenceId={actualEntry.accidentFileId || index}
                                                                typeFilter={['SİGORTA BAŞVURU DİLEKÇESİ']}
                                                                title=""
                                                                canEdit={entryCanEdit}
                                                            />
                                                            {callFormState === insuranceOnlyState && actualEntry.accidentFileId && (
                                                                <Button
                                                                    color="primary"
                                                                    outline
                                                                    size="sm"
                                                                    onClick={() => handleCreateInsuranceApplication(actualEntry.accidentFileId)}
                                                                    disabled={!canEdit || creatingInsuranceRequests[actualEntry.accidentFileId]}
                                                                >
                                                                    {creatingInsuranceRequests[actualEntry.accidentFileId] ? 'İşleniyor...' : 'Yeni Sigorta Başvurusu Ekle'}
                                                                </Button>
                                                            )}
                                                        </div>
                                                    </td>
                                                )}
                                            </tr>
                                        );
                                    });
                                })()}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <p className="text-muted">
                        Henüz çalışılacak dosya eklenmedi.
                    </p>
                )}
            </div>
        </>
    );
};

export default ExpertForm;
